// TableOfContents — "on this page" nav built from a post-mount walk of the
// rendered doc's heading DOM (RFC-0010 §6, PD-0008 G19).
//
// No new backend field: the renderer's `rehype-slug` stamps stable, unique ids
// on every heading, so after the Markdown mounts we query the content container
// for `h2`/`h3` elements and anchor to their ids. The scan re-runs whenever
// `contentKey` changes (i.e. a different doc loads).

import { useEffect, useState, type RefObject } from 'react';

interface TocHeading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  /** Ref to the element wrapping the rendered Markdown (the content container). */
  containerRef: RefObject<HTMLElement | null>;
  /** Changing this re-runs the heading scan — pass the current doc path/body. */
  contentKey?: string;
}

export function TableOfContents({ containerRef, contentKey }: TableOfContentsProps) {
  const [headings, setHeadings] = useState<TocHeading[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      setHeadings([]);
      return;
    }
    const found = Array.from(el.querySelectorAll<HTMLElement>('h2[id], h3[id]'))
      .map((node) => ({
        id: node.id,
        text: (node.textContent ?? '').trim(),
        level: node.tagName === 'H3' ? 3 : 2,
      }))
      .filter((h) => h.id !== '' && h.text !== '');
    setHeadings(found);
  }, [containerRef, contentKey]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label="On this page" className="text-[13px]">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
        On this page
      </p>
      <ul className="flex flex-col gap-1 border-l border-border">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              className={[
                '-ml-px block border-l border-transparent py-0.5 text-text/60 transition-colors hover:border-accent hover:text-text',
                h.level === 3 ? 'pl-5' : 'pl-3',
              ].join(' ')}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
